import * as React from "react";
import type { TimezoneSelectorProps } from "../types";
import "./TimezoneSelector.css";

// =============================================================================
// TimezoneSelector Component
// Toggle between UTC and local timezone
// =============================================================================

export const TimezoneSelector = React.memo(function TimezoneSelector({
  value,
  onChange,
  localTimezone,
  className,
}: TimezoneSelectorProps) {
  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const isUTC = value === "UTC";
  const isLocal = value === localTimezone;

  // Compute local timezone offset string (e.g., "Local (UTC+3)" or "Local (UTC-5:30)")
  const localOffsetLabel = React.useMemo(() => {
    const now = new Date();
    const offsetMinutes = -now.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const offsetSign = offsetMinutes >= 0 ? "+" : "-";
    const offsetStr = offsetMins > 0
      ? `UTC${offsetSign}${offsetHours}:${String(offsetMins).padStart(2, "0")}`
      : `UTC${offsetSign}${offsetHours}`;
    return `Local (${offsetStr})`;
  }, []);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleUTCClick = React.useCallback(() => {
    if (!isUTC) {
      onChange("UTC");
    }
  }, [isUTC, onChange]);

  const handleLocalClick = React.useCallback(() => {
    if (!isLocal) {
      onChange(localTimezone);
    }
  }, [isLocal, localTimezone, onChange]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent, handler: () => void) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`timezone-selector ${className || ""}`}>
      <button
        type="button"
        className={`timezone-selector__button ${isUTC ? "timezone-selector__button--active" : ""}`}
        onClick={handleUTCClick}
        onKeyDown={(e) => handleKeyDown(e, handleUTCClick)}
        aria-pressed={isUTC}
      >
        UTC
      </button>

      <button
        type="button"
        className={`timezone-selector__button ${isLocal ? "timezone-selector__button--active" : ""}`}
        onClick={handleLocalClick}
        onKeyDown={(e) => handleKeyDown(e, handleLocalClick)}
        aria-pressed={isLocal}
      >
        {localOffsetLabel}
      </button>
    </div>
  );
});
